import { ArrowLeft, ShareNetwork } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { categoryCoverUrl } from '@/lib/category-covers'
import { resolveMediaUrl } from '@/lib/media'
import { kuniriIconWeight } from '@/lib/icons'

import type { ActivityDetail } from '../api'

interface ActivityCoverProps {
  activity: ActivityDetail
  canViewSensitive: boolean
  canShare: boolean
  onShare: () => void
}

/**
 * Sliver-style cover: 180px on mobile and 300px from 960px, matching UI_PARITY
 * §3.9. The top bar is pinned: it stays transparent over the artwork until the
 * page scrolls, then gets a solid backdrop so back/share remain reachable.
 */
export function ActivityCover({
  activity,
  canViewSensitive,
  canShare,
  onShare,
}: ActivityCoverProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [pinned, setPinned] = useState(false)
  const src = canViewSensitive
    ? (resolveMediaUrl(activity.coverImageUrl) ??
      categoryCoverUrl(activity.categorySlug, activity.categoryName))
    : categoryCoverUrl(activity.categorySlug, activity.categoryName)

  useEffect(() => {
    const onScroll = () => setPinned(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function goBack() {
    // Browser history state is the React Router index. Deep links start at 0,
    // so they fall back to discovery instead of leaving the app.
    const historyState = window.history.state as { idx?: number } | null
    if (typeof historyState?.idx === 'number' && historyState.idx > 0) {
      navigate(-1)
      return
    }
    navigate('/activities', { replace: true })
  }

  return (
    <>
      <div className="relative h-[180px] w-full overflow-hidden bg-deep min-[960px]:h-[300px]">
        {src ? (
          <img
            src={src}
            alt=""
            className="size-full object-cover"
            loading="eager"
            decoding="async"
          />
        ) : (
          <div
            className="size-full"
            style={{ backgroundColor: activity.categoryColor }}
            aria-hidden="true"
          />
        )}
        <div
          className="absolute inset-0 bg-gradient-to-b from-deep/10 via-transparent to-deep/45"
          aria-hidden="true"
        />
      </div>

      <div
        className={`fixed inset-x-0 top-0 z-40 flex items-start justify-between gap-3 px-4 pt-[calc(var(--safe-top)+12px)] pb-3 transition-colors md:px-6 ${
          pinned
            ? 'bg-white/95 shadow-card backdrop-blur'
            : 'bg-gradient-to-b from-deep/35 to-transparent'
        }`}
      >
        <button
          type="button"
          onClick={goBack}
          className="inline-flex size-11 items-center justify-center rounded-full bg-black/35 text-white shadow-sm ring-1 ring-white/25 backdrop-blur transition-colors hover:bg-black/50"
          aria-label={t('activityDetail.backLabel')}
        >
          <ArrowLeft size={24} weight={kuniriIconWeight} aria-hidden="true" />
        </button>

        {canShare ? (
          <button
            type="button"
            className={`inline-flex size-11 items-center justify-center rounded-full shadow-sm backdrop-blur hover:bg-white ${
              pinned ? 'bg-white text-teal' : 'bg-white/90 text-teal'
            }`}
            aria-label={t('activityDetail.actions.share')}
            onClick={onShare}
          >
            <ShareNetwork
              size={23}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>
    </>
  )
}
